import { BaseService } from './BaseService';
import { apiService } from './apiService';
import {
  VitalityProgressEvent,
  VitalityPartialEvent,
  VitalityRecommendations,
  VitalitySignature,
  VitalityStreamEvent
} from '../types/vitality';

export interface StreamCallbacks {
  onProgress?: (event: VitalityProgressEvent) => void;
  onPartial?: (event: VitalityPartialEvent) => void;
}

export class VitalityService extends BaseService {
  constructor() {
    super('VitalityService', { debug: false });
  }

  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug(`Circuit breaker event: ${eventType}`, data);
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug(`Retrying ${operationName}`, {
      attempt,
      delay,
      error: error.message
    });
  }

  /**
   * Stream personalized recommendations. Calls onProgress / onPartial as
   * NDJSON events arrive; resolves with the final recommendations payload
   * (the `result` event). Rejects if no `result` is emitted before the
   * stream ends, or if an `error` event is received.
   */
  async streamRecommendations(
    callbacks: StreamCallbacks = {},
    signal?: AbortSignal,
    language?: 'en' | 'nl'
  ): Promise<VitalityRecommendations> {
    return this.executeOperation(async () => {
      const path = `/api/users/vitality/recommendations${
        language ? `?language=${encodeURIComponent(language)}` : ''
      }`;
      const response = await apiService.streamGet(path, { signal });

      if (!response.ok) {
        throw new Error(`Vitality stream failed: HTTP ${response.status}`);
      }
      if (!response.body) {
        throw new Error('Vitality stream returned no body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let result: VitalityRecommendations | null = null;
      let lastError: string | null = null;

      const handleLine = (line: string) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        let event: VitalityStreamEvent;
        try {
          event = JSON.parse(trimmed) as VitalityStreamEvent;
        } catch {
          // ignore malformed line
          return;
        }
        switch (event.type) {
          case 'progress':
            callbacks.onProgress?.(event);
            break;
          case 'partial':
            callbacks.onPartial?.(event);
            break;
          case 'result':
            result = event.payload;
            break;
          case 'error':
            lastError = event.message || 'Unknown stream error';
            break;
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl = buffer.indexOf('\n');
        while (nl >= 0) {
          const line = buffer.slice(0, nl);
          buffer = buffer.slice(nl + 1);
          handleLine(line);
          nl = buffer.indexOf('\n');
        }
      }
      if (buffer.length) handleLine(buffer);

      if (lastError && !result) {
        throw new Error(lastError);
      }
      if (!result) {
        throw new Error('Vitality stream ended without a result event');
      }
      return result;
    }, 'streamRecommendations');
  }

  /**
   * Cheap signature check used to decide whether to reuse cached payload.
   */
  async getSignature(language?: 'en' | 'nl'): Promise<VitalitySignature> {
    return this.executeOperation(async () => {
      const path = `/api/users/vitality/signature${
        language ? `?language=${encodeURIComponent(language)}` : ''
      }`;
      return await apiService.get<VitalitySignature>(path);
    }, 'getSignature');
  }
}

export const vitalityService = new VitalityService();
